import { ReactElement, useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import convert from 'convert'
import { isUndefined } from 'lodash'
import numeral from 'numeral'
import {
  TextField as MuiTextField,
  InputProps,
  InputAdornment,
  type TextFieldProps,
} from '@mui/material'
import FormControl from '../FormControl'
import NumberInput from '../NumberInput'

type Unit = 'cm' | 'm' | 'mm' | 'km'

type UnitFieldProps<F> = Omit<TextFieldProps, 'type' | 'variant'> & {
  variant?: TextFieldProps['variant']
  name: F extends object ? Extract<keyof F, string> : string
  placeholder?: string
  label?: string
  inputLabel?: string
  tooltip?: TooltipContents
  InputProps?: InputProps
  staticValue?: number | string
  internalUnit: Unit
  displayUnit: Unit
  numeralFormatter: string
  hideAdornment?: boolean
  /** Skip outer `FormControl` (e.g. Results2 support-grid inline fields with external labels). */
  hideFormControl?: boolean
  formControlFullWidth?: boolean
}

const UnitField = <F,>({
  name,
  rows = 1,
  multiline = false,
  autoFocus = false,
  placeholder = '',
  disabled = false,
  label = '',
  inputLabel,
  InputProps = {},
  numeralFormatter = '0.[00]',
  staticValue,
  tooltip,
  internalUnit,
  displayUnit,
  hideAdornment = false,
  hideFormControl = false,
  formControlFullWidth = true,
  ...textFieldProps
}: UnitFieldProps<F>): ReactElement => {
  const { control } = useFormContext()
  const {
    field: { onChange, onBlur, name: fieldName, ref },
    fieldState: { error },
  } = useController({ name, control })

  const value = useWatch({ name })

  const displayValue = useMemo(() => {
    const usedValue = isUndefined(staticValue) ? value : staticValue

    return !usedValue
      ? usedValue
      : numeral(convert(Number(usedValue), internalUnit).to(displayUnit)).format(numeralFormatter)
  }, [staticValue, value, internalUnit, displayUnit, numeralFormatter])

  const textField = (
    <MuiTextField
      {...textFieldProps}
      variant={textFieldProps.variant ?? 'outlined'}
      onChange={event => {
        const value = event.target.value.length
          ? convert(Number(event.target.value), displayUnit).to(internalUnit)
          : event.target.value
        onChange(value)
      }}
      onBlur={onBlur}
      value={displayValue}
      type="text"
      placeholder={placeholder}
      autoFocus={autoFocus}
      name={fieldName}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      inputRef={ref}
      size="small"
      error={hideFormControl ? Boolean(error) : undefined}
      helperText={hideFormControl ? error?.message : undefined}
      FormHelperTextProps={hideFormControl ? { sx: { mx: 0, mt: 0.25, fontSize: 11 } } : undefined}
      InputProps={{
        ...(hideAdornment
          ? {}
          : { endAdornment: <InputAdornment position="end">{displayUnit}</InputAdornment> }),
        // eslint-disable-next-line
        inputComponent: NumberInput as any,
        ...InputProps,
      }}
      label={inputLabel}
    />
  )

  if (hideFormControl) {
    return textField
  }

  return (
    <FormControl
      label={!inputLabel ? label : undefined}
      error={error?.message}
      tooltip={tooltip}
      fullWidth={formControlFullWidth}
    >
      {textField}
    </FormControl>
  )
}

// some common variations

export { UnitField, type UnitFieldProps }
